import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Users, FileText, Upload, Download, LogOut, Target } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import MentorUsuariosTab from './mentor/MentorUsuariosTab';
import MentorInformesTab from './mentor/MentorInformesTab';
import MentorImportarTab from './mentor/MentorImportarTab';
import MentorExportarTab from './mentor/MentorExportarTab';
import MentorPlan3x3Tab from './mentor/MentorPlan3x3Tab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
    { id: 'usuarios', label: 'Usuarios', icon: Users },
    { id: 'plan3x3', label: 'Plan 3x3', icon: Target },
    { id: 'informes', label: 'Informes', icon: FileText },
    { id: 'importar', label: 'Importar CSV', icon: Upload },
    { id: 'exportar', label: 'Exportar', icon: Download },
];

const MODULE_NAMES = {
    diferencial_inicial: 'Diferencial inicial', registro_episodios: 'Registro episodios',
    semaforo: 'Semáforo', doble_capa: 'Doble capa', linea_base: 'Línea base',
    mapa_amenaza: 'Mapa amenaza', radar_exigencia: 'Radar exigencia',
    contrato_oculto: 'Contrato oculto', arbol_temor: 'Árbol temor', radiografia: 'Radiografía',
    inventario_frentes: 'Inventario frentes', deber_prestado: 'Deber prestado',
    filtro_impacto: 'Filtro impacto', tarea_faro_3: 'Tarea faro', dai: 'DAI',
    descomposicion_momento: 'Descomposición', microsenales: 'Microseñales',
    reescritura: 'Reescritura', intervencion_vivo: 'Intervención', automatizacion: 'Automatización',
    detector_friccion: 'Detector fricción', vmv: 'Versión mínima',
    diseno_entorno: 'Diseño entorno', arranque_obligatorio: 'Arranque obligatorio',
    sistema_ejecucion: 'Sistema ejecución', completado_cap5: 'Cap. 5 completado',
    registro_accion: 'Registro acción', cadena_ejecucion: 'Cadena ejecución',
    escala_ajuste: 'Escala ajuste', metricas_funcionales: 'Métricas funcionales',
    ciclo_progreso: 'Ciclo progreso', completado_cap6: 'Cap. 6 completado',
    evaluacion_funcional: 'Evaluación funcional', mapa_patrones: 'Mapa patrones',
    punto_intervencion: 'Punto intervención', rediseno_patron: 'Rediseño patrón',
    mapa_decision: 'Mapa decisión', completado_cap7: 'Cap. 7 completado',
    diagnostico_sostenibilidad: 'Revisión sostenibilidad', reinicio_rapido: 'Reinicio rápido',
    proteccion_recaidas: 'Protección recaídas', refuerzo_inteligente: 'Refuerzo inteligente',
    sistema_autonomo: 'Sistema autónomo', completado_cap8: 'Cap. 8 completado',
    senales_integracion: 'Señales integración', transferencia_sistema: 'Transferencia sistema',
    criterio_propio: 'Criterio propio', identidad_operativa: 'Identidad operativa',
    vision_operativa: 'Visión operativa', completado_cap9: 'Cap. 9 completado',
    completado: 'Completado'
};

export default function MentorDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('usuarios');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterRisk, setFilterRisk] = useState('all');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserName, setNewUserName] = useState('');
    const [creatingUser, setCreatingUser] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const [passwordCopied, setPasswordCopied] = useState(false);
    const [pendingReports, setPendingReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    // CSV Import state
    const [csvContent, setCsvContent] = useState('');
    const [csvPreview, setCsvPreview] = useState(null);
    const [csvMapping, setCsvMapping] = useState({ email: 'email', name: 'nombre' });
    const [updateExisting, setUpdateExisting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    // Export state
    const [exporting, setExporting] = useState('');

    const fetchUsers = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/mentor/users?include_deleted=true`, { withCredentials: true });
            setUsers(data);
        } catch { toast.error('Error al cargar usuarios'); }
        finally { setLoading(false); }
    }, []);

    const fetchPendingReports = useCallback(async () => {
        setLoadingReports(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/mentor/reports/pending`, { withCredentials: true });
            setPendingReports(data);
        } catch { toast.error('Error al cargar informes'); }
        finally { setLoadingReports(false); }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);
    useEffect(() => { if (activeTab === 'informes') fetchPendingReports(); }, [activeTab, fetchPendingReports]);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreatingUser(true);
        setTempPassword('');
        try {
            const { data } = await axios.post(`${API_URL}/api/mentor/create-user`, { email: newUserEmail, name: newUserName }, { withCredentials: true });
            setTempPassword(data.temp_password);
            toast.success('Usuario creado exitosamente');
            fetchUsers();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al crear usuario'); }
        finally { setCreatingUser(false); }
    };

    const handleCopyPassword = () => { navigator.clipboard.writeText(tempPassword); setPasswordCopied(true); setTimeout(() => setPasswordCopied(false), 2000); };
    const handleLogout = async () => { await logout(); navigate('/login'); };

    const handleSuspend = async (userId) => {
        try { await axios.post(`${API_URL}/api/mentor/user/${userId}/suspend`, {}, { withCredentials: true }); toast.success('Usuario suspendido'); fetchUsers(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };
    const handleReactivate = async (userId) => {
        try { await axios.post(`${API_URL}/api/mentor/user/${userId}/reactivate`, {}, { withCredentials: true }); toast.success('Usuario reactivado'); fetchUsers(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };
    const handleDelete = async (userId) => {
        if (!window.confirm('¿Estás segura de eliminar este usuario? Esta acción es permanente.')) return;
        try { await axios.post(`${API_URL}/api/mentor/user/${userId}/delete`, {}, { withCredentials: true }); toast.success('Usuario eliminado'); fetchUsers(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };

    const handleDownloadPDF = async (userId, userName) => {
        try {
            const response = await axios.get(`${API_URL}/api/mentor/user/${userId}/pdf`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', `SENDA_Informe_${userName.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
            toast.success('PDF descargado');
        } catch { toast.error('Error al descargar PDF'); }
    };

    const handleApproveReport = async (reportId, sendToUser) => {
        try {
            await axios.post(`${API_URL}/api/mentor/report/${reportId}/approve`, { send_to_user: sendToUser }, { withCredentials: true });
            toast.success(sendToUser ? 'Informe aprobado y enviado' : 'Informe aprobado');
            fetchPendingReports();
        } catch { toast.error('Error al aprobar informe'); }
    };

    // CSV handlers
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => { setCsvContent(ev.target.result); setCsvPreview(null); setImportResult(null); };
        reader.readAsText(file);
    };

    const handlePreview = async () => {
        if (!csvContent) { toast.error('Selecciona un archivo CSV'); return; }
        setPreviewing(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/mentor/csv/preview`, { csv_content: csvContent, mapping: csvMapping, update_existing: updateExisting }, { withCredentials: true });
            setCsvPreview(data);
        } catch { toast.error('Error al previsualizar CSV'); }
        finally { setPreviewing(false); }
    };

    const handleImport = async () => {
        if (!csvContent) return;
        setImporting(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/mentor/csv/import`, { csv_content: csvContent, mapping: csvMapping, update_existing: updateExisting }, { withCredentials: true });
            setImportResult(data);
            toast.success(`Importación: ${data.created} creados, ${data.updated} actualizados`);
            fetchUsers();
        } catch { toast.error('Error en importación'); }
        finally { setImporting(false); }
    };

    const handleExport = async (type) => {
        setExporting(type);
        try {
            const response = await axios.get(`${API_URL}/api/mentor/export/${type}`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', `SENDA_export_${type}_${new Date().toISOString().slice(0,10)}.csv`);
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
            toast.success('Exportación descargada');
        } catch { toast.error('Error al exportar'); }
        finally { setExporting(''); }
    };

    return (
        <div className="min-h-screen bg-[#FBFBF9]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <img src="/logo-senda.png" alt="SENDA" className="h-8 sm:h-9 w-auto shrink-0" />
                            <div className="min-w-0">
                                <h1 className="text-lg sm:text-xl font-semibold text-[#1F2933] font-['Outfit'] truncate">Panel Mentor-Psicóloga</h1>
                                <p className="text-xs sm:text-sm text-[#616E7C] truncate">Bienvenida, {user?.name}</p>
                            </div>
                        </div>
                        <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#616E7C] hover:text-[#1F2933] px-2 sm:px-4" data-testid="logout-btn">
                            <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Cerrar sesión</span>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Tab Nav */}
            <div className="bg-white border-b border-[#EAE8E3]/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="flex gap-1 overflow-x-auto" data-testid="mentor-tabs">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                                        activeTab === tab.id ? 'border-[#3E5F6E] text-[#3E5F6E]' : 'border-transparent text-[#9AA5B1] hover:text-[#616E7C]'
                                    }`} data-testid={`tab-${tab.id}`}>
                                    <Icon className="h-4 w-4" /><span className="hidden sm:inline">{tab.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                {activeTab === 'usuarios' && (
                    <MentorUsuariosTab
                        users={users} loading={loading}
                        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                        filterRisk={filterRisk} setFilterRisk={setFilterRisk}
                        showCreateDialog={showCreateDialog} setShowCreateDialog={setShowCreateDialog}
                        newUserName={newUserName} setNewUserName={setNewUserName}
                        newUserEmail={newUserEmail} setNewUserEmail={setNewUserEmail}
                        creatingUser={creatingUser} handleCreateUser={handleCreateUser}
                        tempPassword={tempPassword} handleCopyPassword={handleCopyPassword}
                        passwordCopied={passwordCopied} setTempPassword={setTempPassword}
                        handleDownloadPDF={handleDownloadPDF}
                        handleSuspend={handleSuspend} handleReactivate={handleReactivate} handleDelete={handleDelete}
                        MODULE_NAMES={MODULE_NAMES}
                    />
                )}

                {activeTab === 'plan3x3' && <MentorPlan3x3Tab />}

                {activeTab === 'informes' && (
                    <MentorInformesTab
                        pendingReports={pendingReports} loadingReports={loadingReports}
                        handleApproveReport={handleApproveReport} MODULE_NAMES={MODULE_NAMES}
                    />
                )}

                {activeTab === 'importar' && (
                    <MentorImportarTab
                        csvContent={csvContent} handleFileUpload={handleFileUpload}
                        csvMapping={csvMapping} setCsvMapping={setCsvMapping}
                        updateExisting={updateExisting} setUpdateExisting={setUpdateExisting}
                        handlePreview={handlePreview} previewing={previewing}
                        handleImport={handleImport} importing={importing}
                        csvPreview={csvPreview} importResult={importResult}
                    />
                )}

                {activeTab === 'exportar' && (
                    <MentorExportarTab handleExport={handleExport} exporting={exporting} />
                )}
            </main>
            <SENDAFooter />
        </div>
    );
}
